import type { MercureEvent } from './types'

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function toEventTimestamp(raw: unknown): Date | null {
  if (raw instanceof Date) return raw
  if (typeof raw === 'string' || typeof raw === 'number') {
    const parsed = new Date(raw)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
  // Transport does not always send a timestamp; default to "now" instead of rejecting the event.
  return new Date()
}

/**
 * Runtime guard for inbound Mercure/SSE payloads. The transport layer only
 * guarantees `unknown` JSON - feature code must never receive a trusted
 * `any`/unchecked cast of it. Malformed payloads are dropped (return `null`)
 * instead of throwing, so a single bad frame cannot break the connection.
 */
export function parseMercureEvent(raw: unknown): MercureEvent | null {
  if (!isPlainObject(raw)) return null

  const { type, topic } = raw
  if (typeof type !== 'string' || type.length === 0) return null
  if (!('data' in raw)) return null

  const timestamp = toEventTimestamp(raw['timestamp'])
  if (!timestamp) return null

  const event: MercureEvent = {
    type,
    data: raw['data'],
    timestamp,
  }

  if (typeof topic === 'string') {
    event.topic = topic
  }

  return event
}
